<template>
  <div class="vertical-scroll-indicators">
    <div v-show="canScrollUp" class="vertical-scroll-indicators__edge vertical-scroll-indicators__edge--up">
      <button ref="upButton" type="button" :aria-label="`Scroll ${label} up`" @click="scrollPanel(-1)">
        <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden="true">
          <path d="M1 7L7 1L13 7" stroke="#2f365d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
    <div v-show="canScrollDown" class="vertical-scroll-indicators__edge vertical-scroll-indicators__edge--down">
      <button ref="downButton" type="button" :aria-label="`Scroll ${label} down`" @click="scrollPanel(1)">
        <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden="true">
          <path d="M1 1L7 7L13 1" stroke="#2f365d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

const props = defineProps<{ target: HTMLElement | null; label: string }>()
const canScrollUp = ref(false)
const canScrollDown = ref(false)
const upButton = ref<HTMLButtonElement | null>(null)
const downButton = ref<HTMLButtonElement | null>(null)

const updateScrollState = () => {
  const el = props.target
  const maxScroll = el ? Math.max(0, el.scrollHeight - el.clientHeight) : 0
  // Ignore subpixel rounding and overscroll at either end.
  const top = el ? Math.max(0, Math.min(maxScroll, el.scrollTop)) : 0
  const up = maxScroll > 1 && top > 1
  const down = maxScroll > 1 && top < maxScroll - 1
  // Keep keyboard focus in the panel when an activated arrow disappears.
  if (el && ((!up && document.activeElement === upButton.value) || (!down && document.activeElement === downButton.value))) {
    el.focus({ preventScroll: true })
  }
  canScrollUp.value = up
  canScrollDown.value = down
}

const scrollPanel = (direction: -1 | 1) => {
  const el = props.target
  if (!el) return
  const step = Math.max(80, Math.floor(el.clientHeight * 0.65))
  const top = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + direction * step))
  el.scrollTo({
    top,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
  })
  updateScrollState()
}

watch(() => props.target, (el, _previous, onCleanup) => {
  updateScrollState()
  if (!el) return

  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollState) : null
  const observeContent = () => {
    resizeObserver?.disconnect()
    resizeObserver?.observe(el)
    // Content can grow without changing the scroll viewport's own dimensions.
    for (const child of el.children) resizeObserver?.observe(child)
    updateScrollState()
  }
  const mutationObserver = new MutationObserver(observeContent)
  mutationObserver.observe(el, { childList: true, subtree: true, characterData: true, attributes: true })
  observeContent()
  el.addEventListener('scroll', updateScrollState, { passive: true })
  window.addEventListener('resize', updateScrollState)
  onCleanup(() => {
    resizeObserver?.disconnect()
    mutationObserver.disconnect()
    el.removeEventListener('scroll', updateScrollState)
    window.removeEventListener('resize', updateScrollState)
  })
}, { immediate: true, flush: 'post' })
</script>

<style scoped>
.vertical-scroll-indicators {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 30;
  border-radius: inherit;
  overflow: hidden;
}

.vertical-scroll-indicators__edge {
  position: absolute;
  left: 0;
  right: 0;
  height: 36px;
  display: flex;
  justify-content: center;
}

.vertical-scroll-indicators__edge--up {
  top: 0;
  align-items: flex-start;
  padding-top: 2px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.95), transparent);
}

.vertical-scroll-indicators__edge--down {
  bottom: 0;
  align-items: flex-end;
  padding-bottom: 2px;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.95), transparent);
}

.vertical-scroll-indicators button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #d3d2ce;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.vertical-scroll-indicators button:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
}

.vertical-scroll-indicators button:focus-visible {
  outline: 2px solid var(--studio-focus-ring, #2f7fec);
  outline-offset: 1px;
}

@media (prefers-reduced-motion: reduce) {
  .vertical-scroll-indicators button {
    transition: none;
  }
}
</style>
